import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EditNoteDialog({ open, onClose, note, onSave }) {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && note) {
      setContent(note.content || '');
      setCategory(note.category || '');
      fetchCategories();
    }
  }, [open, note]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
    }
  };

  const handleSave = async () => {
    if (!content.trim() || !category) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_URL}/notes/${note.id}`, {
        content,
        category,
      });

      toast.success('Nota aggiornata con successo!');
      onSave();
      onClose();
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nell\'aggiornamento della nota');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica nota</DialogTitle>
          <DialogDescription>
            Modifica il contenuto o la categoria della nota
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-content">Contenuto *</Label>
            <Textarea
              id="edit-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Modifica il contenuto..."
              className="min-h-[250px] resize-y"
              data-testid="edit-note-content-input"
            />
            <p className="text-xs text-muted-foreground">
              {content.length} caratteri
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-category">Categoria *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12" data-testid="edit-note-category-select">
                <SelectValue placeholder="Seleziona una categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.name} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            data-testid="cancel-edit-button"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="rounded-full"
            data-testid="save-edit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              'Salva modifiche'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}